/**
 * One drawer slot on the right edge (#1922, R6 of
 * `docs/agent-prompts/handover-right-panel-and-inbox-2026-10-02.md`).
 *
 * Every right-edge panel — the lead's next-steps drawer (#1920) and the
 * roster's decision panels (the ladder, Schedule dates, Coverage, Swaps,
 * Versions …) — is a `ui/SideDrawer` fixed at `right-0`. Two of them open at
 * once would sit on top of each other, so they share ONE slot: the panel
 * that opens last takes it and the one that held it closes.
 *
 * - A decision panel claims the slot when it opens (`useDrawerSlot`): it
 *   closes the next-steps drawer (`setPersistentPanelOpen(false)`) and tells
 *   any other open decision panel to close (a window event — the ladder's
 *   state lives in the roster grid, Versions' in the roster panel and the
 *   toolbar surfaces' in the workspace shell, so there is no one owner to
 *   ask).
 * - It yields when another panel claims the slot, or when the next-steps
 *   drawer opens while it is open.
 *
 * Phones never reach this: below `md` the surfaces are bottom sheets and
 * there is no next-steps drawer.
 */
import { useEffect, useId, useRef } from "react";

import { setPersistentPanelOpen, usePersistentPanelOpen } from "./persistentPanel";
import { registerSidePanel } from "./sidePanels";

const CLAIM_EVENT = "tarii:drawer-slot-claim";

/** Take the slot for `ownerId`: close the next-steps drawer and every other
 *  open decision panel. */
export function claimDrawerSlot(ownerId: string): void {
  if (typeof window === "undefined") return;
  setPersistentPanelOpen(false);
  window.dispatchEvent(new CustomEvent<string>(CLAIM_EVENT, { detail: ownerId }));
}

/**
 * Hold the drawer slot while `open`. Claims it on open; calls `onYield` when
 * another panel claims it or the next-steps drawer opens on top.
 */
export function useDrawerSlot(open: boolean, onYield: () => void): void {
  const ownerId = useId();
  // The latest callback, so the listeners below never re-subscribe on a
  // parent re-render (an inline `() => onOpenChange(false)` is new each time).
  const onYieldRef = useRef(onYield);
  onYieldRef.current = onYield;

  useEffect(() => {
    if (open) claimDrawerSlot(ownerId);
  }, [open, ownerId]);

  // #2824 - a panel that holds the slot without being a `ui/SideDrawer` (the
  // docked Solve console) still closes when the demo phone is about to open.
  useEffect(() => {
    if (!open) return;
    return registerSidePanel(() => onYieldRef.current());
  }, [open]);

  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    const onClaim = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== ownerId) onYieldRef.current();
    };
    window.addEventListener(CLAIM_EVENT, onClaim);
    return () => window.removeEventListener(CLAIM_EVENT, onClaim);
  }, [open, ownerId]);

  // The next-steps drawer opening while this panel is open (its handle, by
  // keyboard — the panel covers it for the mouse) takes the slot.
  const [nextStepsOpen] = usePersistentPanelOpen();
  const wasNextStepsOpen = useRef(nextStepsOpen);
  useEffect(() => {
    const opened = nextStepsOpen && !wasNextStepsOpen.current;
    wasNextStepsOpen.current = nextStepsOpen;
    if (open && opened) onYieldRef.current();
  }, [nextStepsOpen, open]);
}
